import { useState } from "react";
import { useLocation, useRoute } from "wouter";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Trash2, Send } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useHidePaidUpgradesOnIos } from "@/hooks/useHidePaidUpgradesOnIos";
import {
  GardenCameraCapture,
  type CapturedPhoto,
} from "@/components/GardenCameraCapture";

type LifecycleCategory =
  | "ornamental"
  | "native"
  | "edible"
  | "houseplant"
  | "weed"
  | "invasive"
  | "unknown";

interface Identification {
  commonName: string;
  latinName: string | null;
  catalogSlug: string | null;
  confidence: "high" | "medium" | "low";
  lifecycleCategory?: LifecycleCategory;
  description?: string | null;
  bloomTime?: string | null;
  bloomDescription?: string | null;
  distinguishingFeatures: string[];
  careTips: string[];
  alternateGuesses: string[];
}

interface JournalEntry {
  id: string;
  photoUrl: string;
  commonName: string;
  catalogSlug: string | null;
  isWeed: boolean;
  userLabel: string | null;
  identificationJson: Identification | null;
  createdAt: string;
}

interface JournalMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  createdAt: string;
}

interface JournalCheckin {
  id: string;
  photoUrl: string;
  analysisJson: {
    growthStage?: string;
    healthFlags?: string[];
    estimatedDaysToHarvest?: number | null;
    notes?: string;
  } | null;
  estimatedHarvestAt: string | null;
  createdAt: string;
}

interface Detail {
  entry: JournalEntry;
  messages: JournalMessage[];
  checkins: JournalCheckin[];
}

const FREE_CHAT_LIMIT = 3;
const FREE_CHECKIN_LIMIT = 3;

function lifecycleLabel(category: LifecycleCategory): string {
  switch (category) {
    case "ornamental": return "Ornamental";
    case "native": return "Native wildflower";
    case "edible": return "Edible";
    case "houseplant": return "Houseplant";
    case "weed": return "Weed";
    case "invasive": return "Invasive";
    default: return "";
  }
}

function exampleImagesUrl(latinName: string | null, commonName: string): string {
  const q = (latinName && latinName.trim()) || commonName;
  return `https://www.google.com/search?tbm=isch&q=${encodeURIComponent(q + " plant")}`;
}

export default function GardenJournalEntry() {
  const hidePaidUpgrades = useHidePaidUpgradesOnIos();
  const [, params] = useRoute<{ id: string }>("/garden/journal/:id");
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const id = params?.id ?? "";
  const [draft, setDraft] = useState("");
  const [pendingPhoto, setPendingPhoto] = useState<CapturedPhoto | null>(null);

  const { data, isLoading, error } = useQuery<Detail>({
    queryKey: [`/api/garden/journal/${id}`],
    queryFn: async () => {
      const res = await fetch(`/api/garden/journal/${id}`, { credentials: "include" });
      if (!res.ok) throw new Error(res.status === 404 ? "Not found" : "Failed");
      return res.json();
    },
    enabled: Boolean(id),
  });

  const { data: billing } = useQuery<{
    subscriptionActive: boolean;
    chatCredits: number;
    photoCredits: number;
  }>({
    queryKey: ["/api/billing/me"],
    queryFn: async () => {
      const res = await fetch("/api/billing/me", { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/garden/journal/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to delete");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/garden/journal"] });
      toast({ title: "Entry deleted" });
      setLocation("/garden/journal");
    },
  });

  const addToGardenMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/garden/journal/${id}/add-to-garden`, {
        method: "POST",
        credentials: "include",
      });
      if (res.status === 412) {
        throw new Error("You need to set up your garden first.");
      }
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "Failed to add");
      }
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Added to your garden", description: "Place it in the planner anytime." });
      queryClient.invalidateQueries({ queryKey: ["/api/garden"] });
    },
    onError: (err: any) => {
      const msg = String(err?.message ?? err);
      toast({
        title: "Couldn't add to garden",
        description: msg,
        action: msg.includes("set up your garden") ? (
          <Button size="sm" onClick={() => setLocation("/garden/setup")}>
            Set up
          </Button>
        ) : undefined,
      });
    },
  });

  const checkinMutation = useMutation({
    mutationFn: async (photoDataUrl: string) => {
      const res = await fetch(`/api/garden/journal/${id}/checkins`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ photoDataUrl }),
      });
      if (res.status === 402) {
        throw new Error(
          "You've used your 3 free check-ins for this plant. Premium upgrades are coming soon.",
        );
      }
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "Failed to add check-in");
      }
      return res.json();
    },
    onSuccess: () => {
      setPendingPhoto(null);
      queryClient.invalidateQueries({ queryKey: [`/api/garden/journal/${id}`] });
      queryClient.invalidateQueries({ queryKey: ["/api/billing/me"] });
    },
    onError: (err: any) => {
      toast({ title: "Couldn't add check-in", description: String(err?.message ?? err) });
    },
  });

  const chatMutation = useMutation({
    mutationFn: async (content: string) => {
      const res = await fetch(`/api/garden/journal/${id}/messages`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content }),
      });
      if (res.status === 402) {
        throw new Error(
          "You've used your 3 free questions for this plant. Premium upgrades are coming soon.",
        );
      }
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "Failed to send");
      }
      return res.json();
    },
    onSuccess: () => {
      setDraft("");
      queryClient.invalidateQueries({ queryKey: [`/api/garden/journal/${id}`] });
      queryClient.invalidateQueries({ queryKey: ["/api/billing/me"] });
    },
    onError: (err: any) => {
      toast({ title: "Couldn't send", description: String(err?.message ?? err) });
    },
  });

  if (isLoading) {
    return (
      <div className="mx-auto max-w-2xl p-4">
        <p className="text-sm text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="mx-auto max-w-2xl space-y-4 p-4">
        <Button variant="outline" size="sm" onClick={() => setLocation("/garden/journal")}>
          Back
        </Button>
        <p className="text-sm text-destructive">Entry not found.</p>
      </div>
    );
  }

  const { entry } = data;
  const ident = entry.identificationJson;
  const userTurnsUsed = data.messages.filter((m) => m.role === "user").length;
  const checkinsUsed = data.checkins.length;
  const subActive = billing?.subscriptionActive ?? false;
  const chatCredits = billing?.chatCredits ?? 0;
  const photoCredits = billing?.photoCredits ?? 0;
  const atFreeCap =
    userTurnsUsed >= FREE_CHAT_LIMIT && !subActive && chatCredits === 0;
  const atCheckinCap =
    checkinsUsed >= FREE_CHECKIN_LIMIT && !subActive && photoCredits === 0;
  const latestCheckin = data.checkins[data.checkins.length - 1];
  const projectedHarvest = latestCheckin?.estimatedHarvestAt ?? null;

  return (
    <div className="mx-auto max-w-2xl space-y-4 p-4 pb-24">
      <div className="flex items-center justify-between">
        <Button variant="outline" size="sm" onClick={() => setLocation("/garden/journal")}>
          Back
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            if (confirm("Delete this entry? This can't be undone.")) {
              deleteMutation.mutate();
            }
          }}
          disabled={deleteMutation.isPending}
        >
          <Trash2 className="mr-1 h-4 w-4" />
          Delete
        </Button>
      </div>

      <Card>
        <CardContent className="p-3">
          <img
            src={entry.photoUrl}
            alt={entry.commonName}
            className="max-h-80 w-full rounded object-contain"
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between text-base">
            <span>{entry.userLabel || entry.commonName}</span>
            {ident ? (
              <Badge variant="secondary">{ident.confidence} confidence</Badge>
            ) : null}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {entry.userLabel ? (
            <div className="text-sm text-muted-foreground">{entry.commonName}</div>
          ) : null}
          {ident?.latinName ? (
            <div className="text-sm italic text-muted-foreground">{ident.latinName}</div>
          ) : null}

          <div className="flex flex-wrap gap-2">
            {entry.isWeed ? (
              <Badge variant="destructive">Likely a weed</Badge>
            ) : null}
            {ident?.lifecycleCategory && ident.lifecycleCategory !== "unknown" ? (
              <Badge variant="outline">{lifecycleLabel(ident.lifecycleCategory)}</Badge>
            ) : null}
          </div>

          {ident?.description ? (
            <p className="text-sm text-muted-foreground">{ident.description}</p>
          ) : null}

          {ident?.bloomTime || ident?.bloomDescription ? (
            <div>
              <div className="mb-1 text-sm font-medium">Blooms</div>
              <p className="text-sm">
                {[ident.bloomTime, ident.bloomDescription].filter(Boolean).join(" — ")}
              </p>
            </div>
          ) : null}

          {ident?.distinguishingFeatures?.length ? (
            <div>
              <div className="mb-1 text-sm font-medium">What we see</div>
              <ul className="list-disc pl-5 text-sm">
                {ident.distinguishingFeatures.map((f, i) => (
                  <li key={i}>{f}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {ident?.careTips?.length ? (
            <div>
              <div className="mb-1 text-sm font-medium">Care tips</div>
              <ul className="list-disc pl-5 text-sm">
                {ident.careTips.map((t, i) => (
                  <li key={i}>{t}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {ident?.alternateGuesses?.length ? (
            <div>
              <div className="mb-1 text-sm font-medium">Could also be</div>
              <div className="flex flex-wrap gap-2">
                {ident.alternateGuesses.map((g, i) => (
                  <Badge key={i} variant="outline">
                    {g}
                  </Badge>
                ))}
              </div>
            </div>
          ) : null}

          <a
            href={exampleImagesUrl(ident?.latinName ?? null, entry.commonName)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block text-sm text-primary underline"
          >
            See more example photos
          </a>

          {entry.catalogSlug && !entry.isWeed ? (
            <Button
              variant="default"
              className="w-full"
              disabled={addToGardenMutation.isPending}
              onClick={() => addToGardenMutation.mutate()}
            >
              {addToGardenMutation.isPending ? "Adding…" : "Add to my garden"}
            </Button>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between text-base">
            <span>Follow-up questions</span>
            {subActive ? (
              <Badge variant="default">Premium</Badge>
            ) : chatCredits > 0 && userTurnsUsed >= FREE_CHAT_LIMIT ? (
              <Badge variant="secondary">{chatCredits} credits</Badge>
            ) : (
              <Badge variant="outline">
                {Math.max(0, FREE_CHAT_LIMIT - userTurnsUsed)} / {FREE_CHAT_LIMIT} free
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {data.messages.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Ask anything about this plant — care, season, eradication, soil, harvest timing.
              You get {FREE_CHAT_LIMIT} free questions per plant.
            </p>
          ) : (
            <div className="space-y-2">
              {data.messages.map((m) => (
                <div
                  key={m.id}
                  className={
                    m.role === "user"
                      ? "ml-8 rounded-lg border bg-muted/40 p-2 text-sm"
                      : "mr-8 rounded-lg border p-2 text-sm"
                  }
                >
                  {m.content}
                </div>
              ))}
            </div>
          )}

          {atFreeCap ? (
            <div className="space-y-2 rounded border border-dashed p-3 text-sm">
              <p className="text-muted-foreground">
                You've used your {FREE_CHAT_LIMIT} free questions for this plant.
              </p>
              {!hidePaidUpgrades && (
                <Button
                  size="sm"
                  className="w-full"
                  onClick={() => setLocation("/upgrade")}
                >
                  Upgrade for more
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <Textarea
                rows={2}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="e.g. How often should I water this?"
                disabled={chatMutation.isPending}
              />
              <Button
                disabled={!draft.trim() || chatMutation.isPending}
                onClick={() => chatMutation.mutate(draft.trim())}
                className="w-full"
              >
                <Send className="mr-2 h-4 w-4" />
                {chatMutation.isPending ? "Asking…" : "Send"}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between text-base">
            <span>Progress check-ins</span>
            {subActive ? (
              <Badge variant="default">Premium</Badge>
            ) : photoCredits > 0 && checkinsUsed >= FREE_CHECKIN_LIMIT ? (
              <Badge variant="secondary">{photoCredits} credits</Badge>
            ) : (
              <Badge variant="outline">
                {Math.max(0, FREE_CHECKIN_LIMIT - checkinsUsed)} / {FREE_CHECKIN_LIMIT} free
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {projectedHarvest ? (
            <div className="rounded border bg-muted/30 p-2 text-sm">
              <span className="font-medium">Estimated harvest:</span>{" "}
              {new Date(projectedHarvest).toLocaleDateString()} (based on the latest check-in)
            </div>
          ) : null}

          {data.checkins.length > 0 ? (
            <div className="space-y-2">
              {data.checkins.map((c) => (
                <div key={c.id} className="flex gap-3 rounded border p-2">
                  <img
                    src={c.photoUrl}
                    alt="Check-in"
                    className="h-20 w-20 flex-none rounded object-cover"
                  />
                  <div className="flex-1 space-y-1 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">
                        {c.analysisJson?.growthStage ?? "Stage unknown"}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(c.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    {c.analysisJson?.notes ? (
                      <div className="text-muted-foreground">{c.analysisJson.notes}</div>
                    ) : null}
                    {c.analysisJson?.healthFlags?.length ? (
                      <div className="flex flex-wrap gap-1">
                        {c.analysisJson.healthFlags.map((flag, i) => (
                          <Badge key={i} variant="destructive" className="text-xs">
                            {flag}
                          </Badge>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          {atCheckinCap ? (
            <div className="space-y-2 rounded border border-dashed p-3 text-sm">
              <p className="text-muted-foreground">
                You've used your {FREE_CHECKIN_LIMIT} free check-ins for this plant.
              </p>
              {!hidePaidUpgrades && (
                <Button
                  size="sm"
                  className="w-full"
                  onClick={() => setLocation("/upgrade")}
                >
                  Upgrade for more
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <GardenCameraCapture
                onCaptured={(p) => setPendingPhoto(p)}
                label={
                  data.checkins.length === 0
                    ? "Add a check-in photo to start tracking growth and harvest timing."
                    : "Add another check-in photo."
                }
              />
              {pendingPhoto ? (
                <>
                  <img
                    src={pendingPhoto.dataUrl}
                    alt="Pending check-in"
                    className="max-h-48 w-full rounded border object-contain"
                  />
                  <Button
                    disabled={checkinMutation.isPending}
                    onClick={() => checkinMutation.mutate(pendingPhoto.dataUrl)}
                    className="w-full"
                  >
                    {checkinMutation.isPending ? "Analyzing…" : "Save check-in"}
                  </Button>
                </>
              ) : null}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
